import { useCallback } from 'react';
import analytics from 'modules/analytics';
import { worklist } from 'modules/analytics/constants';
import { CASE_ACTIONS } from 'config/constants';
import { sendCrossTabEvent, NameSpaces, CrossWindowDataRefetchType } from 'modules/EventsManager';
import { useSetRecoilState } from 'recoil';
import {
  claimingWorklistSmidState,
  draftReportWorklistSmid,
} from '../domains/reporter/Reporter/state';
import { ApolloError, useMutation } from '@apollo/client';
import {
  CHANGE_WORKLIST_ITEM_STATUS,
  GET_CURRENT_WORKLIST_REPORT,
  GET_WORKLIST_ITEM,
} from '../modules/Apollo/queries';
import type {
  GetWorklistItemQuery,
  GetWorklistItemQueryVariables,
  ChangeWorklistItemStatusMutation,
  ChangeWorklistItemStatusMutationVariables,
  GetCurrentWorklistReportQuery,
  GetCurrentWorklistReportQueryVariables,
} from 'generated/graphql';
import { logger } from 'modules/logger';
import useStudyIds from './useStudyIds';
import {
  useHasClaimExamPermission,
  LOCKED_CASE_START_READ_WARNING,
} from './useHasClaimExamPermission';
import { useOpenCase } from './useOpenCase';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { useBroadcastRefreshCurrentCase } from 'common/CurrentCase/useBroadcastRefreshCurrentCase';
import { useCurrentCaseClaimedStatus } from './useCurrentCaseClaimedStatus';
import { WithApolloContext } from 'modules/Apollo/types';

export { LOCKED_CASE_START_READ_WARNING };

// The backend rejects re-claiming a case with a 409 whose message names the blocking status,
// e.g. "status - Countdown" or "status - Addendum Countdown". `report`/`report.addendums`
// timestamps on the client can't reliably predict this: the Reporter (where a submit starts the
// countdown) and the Viewer (where useClaimPatient runs) are separate windows with separate
// Apollo caches, and nothing refetches the Viewer's cache when an addendum is submitted. So we
// let the backend's live response drive this instead of predicting it client-side.
const isSigningCountdownRejection = (error: unknown): boolean =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(
    (graphQLError) =>
      graphQLError.extensions?.code === 'BAD_USER_INPUT' && /countdown/i.test(graphQLError.message)
  );

/**
 * The hook is used to claim a patient (and start reading a case).
 * This is for use outside of the Worklist domain.
 */

type UseClaimPatientReturn = {
  claimPatient: (args?: {
    overrideCaseId?: Nullable<string>;
    overrideCaseGroupId?: Nullable<string>;
  }) => Promise<void>;
  startRead: (args?: {
    overrideCaseId?: Nullable<string>;
    overrideCaseGroupId?: Nullable<string>;
    overrideStudyIds?: Nullable<Array<string>>;
  }) => Promise<void>;
};

export const useClaimPatient = ({ context }: WithApolloContext): UseClaimPatientReturn => {
  const { currentCaseId, currentCase } = useCurrentCaseContext();
  const broadcastRefreshCase = useBroadcastRefreshCurrentCase();
  const setDraftReportWorklistSmid = useSetRecoilState(draftReportWorklistSmid);
  const setClaimingWorklistSmid = useSetRecoilState(claimingWorklistSmidState);
  const studyIds = useStudyIds({ context });
  const { openCase } = useOpenCase({ context });
  const { enqueueToast } = useToasterDispatch();
  const hasClaimExamPermission = useHasClaimExamPermission();
  const { isClaimedByMe } = useCurrentCaseClaimedStatus({ context });

  const [changeWorklistItemStatus] = useMutation<
    ChangeWorklistItemStatusMutation,
    ChangeWorklistItemStatusMutationVariables
  >(CHANGE_WORKLIST_ITEM_STATUS, {
    // Keep the mutation promise open until refetchQueries resolve.
    // This gives claim-dependent flows (e.g. template autoload) a reliable hydration boundary.
    awaitRefetchQueries: true,
    refetchQueries: (result) => {
      const smid = result.data?.changeWorklistItemStatus?.smid;
      if (smid == null) return [];
      return [
        { query: GET_WORKLIST_ITEM, variables: { smid } },
        { query: GET_CURRENT_WORKLIST_REPORT, variables: { smid } },
      ];
    },
    onCompleted: async (data) => {
      await broadcastRefreshCase();

      sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
        type: CrossWindowDataRefetchType.WORKLIST,
      });

      analytics.track(worklist.usr.addToQueue, {
        worklistItemSmid: data.changeWorklistItemStatus?.smid,
      });
    },
    update: (proxy, data) => {
      const worklistSmid = data.data?.changeWorklistItemStatus?.smid;
      const user = data.data?.changeWorklistItemStatus?.claimedBy;

      if (worklistSmid != null && user) {
        const worklistItem =
          proxy.readQuery<GetWorklistItemQuery, GetWorklistItemQueryVariables>({
            query: GET_WORKLIST_ITEM,
            variables: { smid: worklistSmid },
          }) || {};

        if (worklistItem.worklistItem != null) {
          const item = worklistItem.worklistItem;
          logger.info('[useClaimPatient]: update get worklist item claimed status');
          proxy.writeQuery<GetWorklistItemQuery, GetWorklistItemQueryVariables>({
            query: GET_WORKLIST_ITEM,
            variables: {
              smid: worklistSmid,
            },
            data: {
              worklistItem: {
                ...item,
                claimedBy: {
                  id: user.id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                },
              },
            },
          });
        }

        const currentWorklistReport =
          proxy.readQuery<GetCurrentWorklistReportQuery, GetCurrentWorklistReportQueryVariables>({
            query: GET_CURRENT_WORKLIST_REPORT,
            variables: { smid: worklistSmid },
          }) || {};

        if (currentWorklistReport.worklistItem) {
          const item = currentWorklistReport.worklistItem;
          logger.info('[useClaimPatient]: update current worklist report claimed status');

          proxy.writeQuery<GetCurrentWorklistReportQuery, GetCurrentWorklistReportQueryVariables>({
            query: GET_CURRENT_WORKLIST_REPORT,
            variables: {
              smid: worklistSmid,
            },
            data: {
              worklistItem: {
                ...item,
                claimedBy: {
                  id: user.id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                },
              },
            },
          });
        }
      }
    },
    context,
  });

  const claimPatient = useCallback(
    async (args?: {
      overrideCaseId?: Nullable<string>;
      overrideCaseGroupId?: Nullable<string>;
    }) => {
      const { overrideCaseId = null, overrideCaseGroupId = null } = args || {};

      // Safety net: the backend is the authoritative claim gate, but never issue a claim from a
      // user whose role lacks CLAIM_EXAM. The UI surfaces that call this are disabled separately.
      if (hasClaimExamPermission === false) {
        logger.info('[useClaimPatient]: claim skipped — user lacks CLAIM_EXAM permission');
        return;
      }

      const caseGroupId = overrideCaseGroupId ?? currentCase?.groupId;
      const caseId = overrideCaseId ?? currentCaseId;
      if (caseId != null) {
        if (caseGroupId != null) {
          const message =
            'The exam you claimed is grouped with other exams. Those exams have been claimed as well.';
          enqueueToast(message);
        }
        // Combined with `refetchQueries` & `awaitRefetchQueries: true` above, this keeps template autoload pending
        // until GET_CURRENT_WORKLIST_REPORT refetches settle with the updated report template
        setClaimingWorklistSmid(caseId);
        try {
          await changeWorklistItemStatus({
            variables: {
              smid: caseId,
              claim: true,
            },
          });
        } finally {
          // Only clear if this case still owns the marker.
          // This prevents overlapping claims from clearing a newer in-flight state.
          setClaimingWorklistSmid((pendingClaimSmid) =>
            pendingClaimSmid === caseId ? null : pendingClaimSmid
          );
        }
      }
    },
    [
      hasClaimExamPermission,
      currentCase,
      currentCaseId,
      changeWorklistItemStatus,
      enqueueToast,
      setClaimingWorklistSmid,
    ]
  );

  const startRead = useCallback(
    async (args?: {
      overrideCaseId?: Nullable<string>;
      overrideCaseGroupId?: Nullable<string>;
      overrideStudyIds?: Nullable<Array<string>>;
    }) => {
      const {
        overrideCaseId = null,
        overrideCaseGroupId = null,
        overrideStudyIds = null,
      } = args || {};

      try {
        await claimPatient({ overrideCaseId, overrideCaseGroupId });
      } catch (error) {
        // If we already own this claim and the only reason the re-claim was rejected is that
        // the case is mid-signing, there's nothing left to claim — swallow the rejection and
        // fall through to reopening the Reporter. `overrideCaseId` targets a different case
        // (autoload), so this never applies there. Any other failure (including this same
        // rejection when the case is claimed by someone else) still propagates.
        const canSkipSigningCountdownRejection =
          overrideCaseId == null && isClaimedByMe && isSigningCountdownRejection(error);

        logger.info('[useClaimPatient]: startRead claim rejected', {
          canSkipSigningCountdownRejection,
          overrideCaseId,
          isClaimedByMe,
        });

        if (!canSkipSigningCountdownRejection) {
          throw error;
        }
      }

      const caseId = overrideCaseId ?? currentCaseId;

      if (caseId != null) {
        analytics.startReadSession(caseId);
        openCase({
          smid: caseId,
          action: CASE_ACTIONS.READ,
          studyIds: overrideStudyIds ?? studyIds,
        });
        setDraftReportWorklistSmid(null);
      }
    },
    [claimPatient, currentCaseId, isClaimedByMe, openCase, setDraftReportWorklistSmid, studyIds]
  );

  return {
    claimPatient,
    startRead,
  };
};
